// Teacher-memory metadata shared between server (tools, mutation) and client (profile UI).
// Keep this module free of server-only imports so the client can import it safely.

/** Max rules kept per category; enforced on every save (agent curation and manual profile edits). */
export const MAX_RULES_PER_CATEGORY = 10;

/** The tool-facing category keys (see save_teacher_memory `memory_categories`). */
export type MemoryCategoryKey =
  | 'chat_and_workflow'
  | 'grading_and_feedback'
  | 'master_solution_design'
  | 'grading_scheme_creation';

/** The persisted field on the embedded Memory entity backing each category. */
export type MemoryField = 'chattingStyle' | 'gradingStyle' | 'masterSolutionStyle' | 'gradingSchemeStyle';

export interface MemoryCategoryMeta {
  /** Tool-facing category key. */
  key: MemoryCategoryKey;
  /** Field on the Memory entity holding this category's rules. */
  field: MemoryField;
  /** i18n key suffix under `Profile.rules.categories.*` for the localized label. */
  i18nKey: string;
}

/**
 * The four memory categories in the order they are shown in the profile. `chat_and_workflow` is
 * first because it is the only category that steers the assistant's own chat behaviour directly.
 */
export const MEMORY_CATEGORIES: readonly MemoryCategoryMeta[] = [
  { key: 'chat_and_workflow', field: 'chattingStyle', i18nKey: 'chat_and_workflow' },
  { key: 'grading_and_feedback', field: 'gradingStyle', i18nKey: 'grading_and_feedback' },
  { key: 'master_solution_design', field: 'masterSolutionStyle', i18nKey: 'master_solution_design' },
  { key: 'grading_scheme_creation', field: 'gradingSchemeStyle', i18nKey: 'grading_scheme_creation' },
];

/** All persisted Memory fields, in display order. */
export const MEMORY_FIELDS: readonly MemoryField[] = MEMORY_CATEGORIES.map(c => c.field);

/** Sentinel `subject` value for a rule that applies across all subjects. */
export const GENERAL_SUBJECT = 'general' as const;
